'use client'

// Auth surfaces use inline layout styles but keep every product color on the
// shared semantic tokens so login, signup, and recovery inherit both themes.

import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import { forwardRef, useId, useState } from 'react'
import { useTranslations } from 'next-intl'
import { Eye, EyeSlash } from '@/components/icons'
import { authFontStacks } from './auth-shell'
import styles from './auth-shell.module.css'
import { Spinner } from '@/components/ui/spinner'

const { heading: HEADING, body: BODY } = authFontStacks

const AUTH_TOKENS = {
  foreground: 'hsl(var(--foreground))',
  mutedForeground: 'hsl(var(--muted-foreground))',
  background: 'hsl(var(--card))',
  border: 'hsl(var(--border))',
  borderFaint: 'var(--hairline-faint)',
  brand: 'hsl(var(--brand))',
  brandSoft: 'var(--brand-soft)',
  brandSoftStrong: 'color-mix(in oklab, hsl(var(--brand)) 16%, transparent)',
  destructive: 'hsl(var(--destructive))',
  destructiveSoft: 'var(--danger-soft)',
} as const

export function AuthHeading({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <h1
        style={{
          fontFamily: HEADING,
          fontWeight: 500,
          fontSize: '28px',
          color: AUTH_TOKENS.foreground,
          letterSpacing: '-0.02em',
          lineHeight: 1.1,
          margin: 0,
        }}
      >
        {title}
      </h1>
      <p
        style={{
          fontFamily: BODY,
          fontWeight: 400,
          fontSize: '14px',
          color: AUTH_TOKENS.mutedForeground,
          letterSpacing: '-0.005em',
          lineHeight: 1.5,
          margin: 0,
        }}
      >
        {subtitle}
      </p>
    </div>
  )
}

interface AuthFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  id: string
  label: ReactNode
  trailingLabel?: ReactNode
  fieldStyle?: React.CSSProperties
  inputSuffix?: ReactNode
}

export const AuthField = forwardRef<HTMLInputElement, AuthFieldProps>(function AuthField(
  { id, label, trailingLabel, style, fieldStyle, inputSuffix, ...rest },
  ref
) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <label
          htmlFor={id}
          style={{
            fontFamily: BODY,
            fontWeight: 500,
            fontSize: '13px',
            color: AUTH_TOKENS.foreground,
          }}
        >
          {label}
        </label>
        {trailingLabel ?? null}
      </div>
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <input
          ref={ref}
          id={id}
          {...rest}
          style={{
            width: '100%',
            height: '48px',
            padding: inputSuffix ? '0 48px 0 14px' : '0 14px',
            border: `1px solid ${AUTH_TOKENS.border}`,
            borderRadius: '12px',
            backgroundColor: AUTH_TOKENS.background,
            fontFamily: BODY,
            fontWeight: 400,
            // 16px control size: native inputs stay at 16px so touch devices
            // don't trigger focus zoom (text-control rule).
            fontSize: '16px',
            color: AUTH_TOKENS.foreground,
            letterSpacing: '-0.005em',
            outline: 'none',
            transition: 'border-color 120ms var(--ease-out), box-shadow 120ms var(--ease-out)',
            ...style,
            ...fieldStyle,
          }}
          onFocus={(event) => {
            event.currentTarget.style.borderColor = AUTH_TOKENS.brand
            event.currentTarget.style.boxShadow = `0 0 0 3px ${AUTH_TOKENS.brandSoft}`
            rest.onFocus?.(event)
          }}
          onBlur={(event) => {
            event.currentTarget.style.borderColor = AUTH_TOKENS.border
            event.currentTarget.style.boxShadow = 'none'
            rest.onBlur?.(event)
          }}
        />
        {inputSuffix}
      </div>
    </div>
  )
})

interface AuthPasswordFieldProps extends Omit<
  AuthFieldProps,
  'type' | 'fieldStyle' | 'inputSuffix'
> {
  showToggle?: boolean
}

export function AuthPasswordField({ showToggle = true, ...rest }: AuthPasswordFieldProps) {
  const t = useTranslations('Auth')
  const [visible, setVisible] = useState(false)
  const reactId = useId()
  const toggleId = `${reactId}-toggle`

  const toggle = showToggle ? (
    <button
      id={toggleId}
      type="button"
      aria-label={visible ? t('hidePassword') : t('showPassword')}
      onClick={() => setVisible((v) => !v)}
      style={{
        position: 'absolute',
        right: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '28px',
        height: '28px',
        border: 'none',
        background: 'transparent',
        color: AUTH_TOKENS.mutedForeground,
        cursor: 'pointer',
        borderRadius: '6px',
      }}
    >
      {visible ? <EyeSlash size={18} weight="regular" /> : <Eye size={18} weight="regular" />}
    </button>
  ) : null

  return (
    <AuthField
      {...rest}
      type={visible ? 'text' : 'password'}
      inputSuffix={toggle}
      fieldStyle={{ letterSpacing: visible ? '-0.005em' : '0.08em' }}
    />
  )
}

interface AuthPrimaryButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  isLoading?: boolean
  children: ReactNode
}

export function AuthPrimaryButton({
  isLoading,
  children,
  disabled,
  style,
  ...rest
}: AuthPrimaryButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      className={styles.pressable}
      disabled={disabled || isLoading}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        height: '52px',
        width: '100%',
        backgroundColor: AUTH_TOKENS.brand,
        color: AUTH_TOKENS.background,
        border: 'none',
        borderRadius: '12px',
        fontFamily: BODY,
        fontWeight: 500,
        fontSize: '14px',
        cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
        opacity: disabled || isLoading ? 0.75 : 1,
        boxShadow: `0 1px 2px ${AUTH_TOKENS.brandSoft}, 0 8px 24px ${AUTH_TOKENS.brandSoftStrong}`,
        transition:
          'transform 120ms var(--ease-out), box-shadow 120ms var(--ease-out), opacity 120ms var(--ease-out)',
        ...style,
      }}
    >
      {isLoading ? <Spinner size={18} /> : null}
      {children}
    </button>
  )
}

interface AuthGoogleButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string
}

export function AuthGoogleButton({ label, disabled, style, ...rest }: AuthGoogleButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      className={styles.pressable}
      disabled={disabled}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        height: '52px',
        width: '100%',
        backgroundColor: AUTH_TOKENS.background,
        color: AUTH_TOKENS.foreground,
        border: `1px solid ${AUTH_TOKENS.border}`,
        borderRadius: '12px',
        fontFamily: BODY,
        fontWeight: 500,
        fontSize: '14px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition:
          'transform 120ms var(--ease-out), background-color 120ms var(--ease-out), border-color 120ms var(--ease-out)',
        ...style,
      }}
    >
      <svg width={18} height={18} viewBox="0 0 48 48" style={{ flexShrink: 0 }} aria-hidden="true">
        <path
          fill="#FFC107"
          d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
        />
        <path
          fill="#FF3D00"
          d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
        />
        <path
          fill="#4CAF50"
          d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
        />
        <path
          fill="#1976D2"
          d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
        />
      </svg>
      {label}
    </button>
  )
}

export function AuthDivider({ label }: { label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '14px',
        width: '100%',
      }}
    >
      <div style={{ flex: 1, height: '1px', backgroundColor: AUTH_TOKENS.borderFaint }} />
      <span
        style={{
          fontFamily: BODY,
          fontWeight: 400,
          fontSize: '12px',
          color: AUTH_TOKENS.mutedForeground,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      <div style={{ flex: 1, height: '1px', backgroundColor: AUTH_TOKENS.borderFaint }} />
    </div>
  )
}

export function AuthError({ children }: { children: ReactNode }) {
  return (
    // data-enter (globals.css): fade + translateY rise on mount so the banner
    // doesn't pop in and shove layout with zero bridge. Entry only — errors
    // clear instantly.
    <div
      role="alert"
      data-enter=""
      data-testid="auth-form-error"
      style={{
        padding: '12px 14px',
        borderRadius: '10px',
        backgroundColor: AUTH_TOKENS.destructiveSoft,
        color: AUTH_TOKENS.destructive,
        fontFamily: BODY,
        fontWeight: 500,
        fontSize: '13px',
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  )
}

export function AuthDemoFooter({
  prompt,
  linkLabel,
  href = 'https://trellistech.com/demo',
}: {
  prompt: string
  linkLabel: string
  href?: string
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        width: '100%',
      }}
    >
      <span
        style={{
          fontFamily: BODY,
          fontWeight: 400,
          fontSize: '14px',
          color: AUTH_TOKENS.mutedForeground,
        }}
      >
        {prompt}
      </span>
      <a
        href={href}
        rel="noopener noreferrer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          fontFamily: BODY,
          fontWeight: 500,
          fontSize: '14px',
          color: AUTH_TOKENS.foreground,
          textDecoration: 'none',
        }}
      >
        {linkLabel}
        <svg
          width={12}
          height={12}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1={5} y1={12} x2={19} y2={12} />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </a>
    </div>
  )
}
